import { useEffect, useMemo, useRef } from 'react';
import { Camera, ImagePlus, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WorkPhotoCaptureProps {
  files: File[];
  onChange: (files: File[]) => void;
  max?: number;
  disabled?: boolean;
  className?: string;
}

/**
 * Camera-first photo picker. On a tablet `capture="environment"` opens the
 * rear camera straight away; on a laptop it falls back to the file picker.
 * Thumbnails are object URLs, revoked on change / unmount.
 */
export function WorkPhotoCapture({ files, onChange, max = 6, disabled, className }: WorkPhotoCaptureProps) {
  const cameraRef = useRef<HTMLInputElement | null>(null);
  const libraryRef = useRef<HTMLInputElement | null>(null);

  const previews = useMemo(() => files.map((f) => ({ file: f, url: URL.createObjectURL(f) })), [files]);
  useEffect(() => {
    return () => {
      for (const p of previews) URL.revokeObjectURL(p.url);
    };
  }, [previews]);

  const add = (list: FileList | null) => {
    if (!list) return;
    const next = [...files];
    for (const f of Array.from(list)) {
      if (next.length >= max) break;
      if (f.type.startsWith('image/')) next.push(f);
    }
    onChange(next);
  };

  const remove = (idx: number) => onChange(files.filter((_, i) => i !== idx));

  const full = files.length >= max;

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-wrap gap-3">
        {previews.map((p, i) => (
          <div key={p.url} className="relative h-24 w-24 overflow-hidden rounded-xl border border-border bg-secondary">
            <img src={p.url} alt={`Photo ${i + 1}`} className="h-full w-full object-cover" />
            <button
              type="button"
              onClick={() => remove(i)}
              disabled={disabled}
              aria-label={`Remove photo ${i + 1}`}
              className="absolute right-1 top-1 flex h-7 w-7 items-center justify-center rounded-full bg-humus/80 text-white hover:bg-humus"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ))}
        {!full && (
          <>
            <button
              type="button"
              disabled={disabled}
              onClick={() => cameraRef.current?.click()}
              className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border bg-card text-muted-foreground hover:bg-secondary disabled:opacity-50"
            >
              <Camera className="h-6 w-6" />
              <span className="text-xs font-medium">Camera</span>
            </button>
            <button
              type="button"
              disabled={disabled}
              onClick={() => libraryRef.current?.click()}
              className="flex h-24 w-24 flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-border bg-card text-muted-foreground hover:bg-secondary disabled:opacity-50"
            >
              <ImagePlus className="h-6 w-6" />
              <span className="text-xs font-medium">Library</span>
            </button>
          </>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        {files.length} / {max} photos
      </p>
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(e) => {
          add(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={libraryRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          add(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}
